import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Layers, EyeOff, GitBranch, ShieldCheck, TrendingDown, Target,
  AlertTriangle, ChevronRight,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

/**
 * Rationalisation Progress — embedded on the Apps Dashboard only. The
 * Discover page keeps its own tiles.
 *
 * Data source: GET /api/dashboard/rationalisation-progress
 *  - scope: in-scope vs excluded families (excluded by reason), version debt
 *  - managed: devices on the version ALICE released (rollouts in progress
 *    and governance-excluded versions left out of the percentage)
 *  - unmanaged: devices on the newest version seen on the estate
 *  - history: one point per day, recorded at each recompute and sync
 *
 * The rules behind the figures are documented in
 * backend/services/rationalisation_progress.py.
 */

const ACCENT = {
  cyan:    '#00D4E4',
  emerald: '#10B981',
  amber:   '#F59E0B',
  rose:    '#F43F5E',
  purple:  '#8B5CF6',
  blue:    '#6376F0',
  slate:   '#94A3B8',
};

const CARD = 'bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]';

const TOOLTIP_STYLE = {
  contentStyle: {
    background: 'rgba(15, 23, 42, 0.95)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: 8,
    color: '#fff',
    fontSize: 12,
    padding: '8px 12px',
  },
  itemStyle:  { color: '#fff' },
  labelStyle: { color: 'rgba(255,255,255,0.75)', fontWeight: 600, marginBottom: 4 },
};

const REASON_LABELS = {
  system_component:   'system components',
  device_driver:      'device drivers',
  non_business:       'non-business',
  redistributable:    'redistributables',
  help_files:         'help files',
  deprecated:         'deprecated',
  installation_files: 'installation files',
  bundle:             'bundles',
  web_shortcut:       'web shortcuts',
};

const fmt = (n) => (n ?? 0).toLocaleString();
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtShortDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

const KPI = ({ label, value, sub, icon: Icon, accent, testid }) => (
  <Card className={CARD} data-testid={testid}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs font-medium uppercase tracking-wider text-white/80">{label}</span>
        {Icon && (
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}20` }}>
            <Icon className="w-4 h-4" style={{ color: accent }} />
          </div>
        )}
      </div>
      <div className="text-3xl font-bold text-white leading-none">{value}</div>
      {sub && <div className="text-xs text-white/70 mt-2">{sub}</div>}
    </CardContent>
  </Card>
);

// "↓ 64 since 1 Oct 2026" against the first recorded day. Lower is better
// for both figures it's used on, so a fall reads green.
const DeltaSince = ({ current, baselineValue, baselineDate }) => {
  if (baselineValue == null || !baselineDate) return null;
  const delta = current - baselineValue;
  if (delta === 0) return <span>unchanged since {fmtDate(baselineDate)}</span>;
  return (
    <span className={delta < 0 ? 'text-emerald-400' : 'text-amber-400'}>
      {delta < 0 ? '↓' : '↑'} {fmt(Math.abs(delta))} since {fmtDate(baselineDate)}
    </span>
  );
};

// One stacked bar: on target / rolling out / behind.
const ControlBar = ({ segments, testid }) => {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0);
  return (
    <div data-testid={testid}>
      <div className="h-2.5 bg-[rgba(229,239,250,0.08)] rounded-full overflow-hidden flex">
        {total > 0 && segments.map((s) => (
          s.value > 0 ? (
            <div
              key={s.label}
              className="h-full transition-all"
              style={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color }}
              title={`${s.label}: ${fmt(s.value)} devices`}
            />
          ) : null
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-white/80">
        {segments.map((s) => (
          <span key={s.label} className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
            {s.label} <span className="text-white">{fmt(s.value)}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const ControlFigure = ({ title, pct, caption, segments, measured, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    <div className="text-sm text-white/90">{title}</div>
    <div className="flex items-baseline gap-2">
      <span className="text-4xl font-bold text-white">{pct == null ? '—' : `${pct}%`}</span>
    </div>
    <div className="text-xs text-white/70">{caption}</div>
    <ControlBar segments={segments} testid={`${testid}-bar`} />
    {measured === false && (
      <div className="text-[11px] text-white/60">
        Some apps report no device names, so those are estimated from install counts.
      </div>
    )}
  </div>
);

const FamilyTable = ({ rows, columns, empty, onRow, testid }) => (
  rows.length ? (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead>
          <tr className="text-left text-xs uppercase text-white/60 border-b border-[rgba(229,239,250,0.1)]">
            {columns.map((c) => (
              <th key={c.key} className={`px-5 py-3 font-medium ${c.right ? 'text-right' : ''}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.family_name}
              className="border-b border-[rgba(229,239,250,0.05)] hover:bg-white/[0.04] transition-colors cursor-pointer"
              onClick={() => onRow(r)}
            >
              {columns.map((c) => (
                <td key={c.key} className={`px-5 py-3 ${c.right ? 'text-right' : ''} ${c.className || 'text-white/80'}`}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <p className="text-white/60 text-sm px-5 py-6">{empty}</p>
  )
);

const RationalisationProgress = () => {
  const navigate = useNavigate();
  const { getAccessToken, demoMode } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const headers = {};
        if (!demoMode) {
          const token = await getAccessToken();
          if (token) headers.Authorization = `Bearer ${token}`;
        }
        const res = await axios.get(`${API}/dashboard/rationalisation-progress`, { headers });
        if (!cancelled) setData(res.data);
      } catch (e) {
        console.error('RationalisationProgress load failed', e);
        if (!cancelled) setError('Could not load rationalisation progress.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [getAccessToken, demoMode]);

  if (loading) {
    return (
      <div className="mt-10 flex items-center justify-center min-h-[200px]" data-testid="rationalisation-progress-loading">
        <div className="w-10 h-10 rounded-full border-4 border-[rgba(229,239,250,0.1)] border-t-cyan-400 animate-spin" />
      </div>
    );
  }

  if (error || !data?.scope || !data.scope.total_families) {
    return (
      <div className="mt-10 text-white/70 text-sm" data-testid="rationalisation-progress-error">
        {error || 'No rationalisation data yet. Run a sync from Discover.'}
      </div>
    );
  }

  const scope = data.scope;
  const managed = data.managed || {};
  const unmanaged = data.unmanaged || {};
  const history = data.history || [];
  const baseline = data.baseline;
  const today = history.length ? history[history.length - 1].date : null;
  // Only show a delta once there's a day to compare with.
  const hasBaseline = baseline && baseline.date !== today;

  const topReasons = Object.entries(scope.excluded_by_reason || {})
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([k, n]) => `${fmt(n)} ${REASON_LABELS[k] || k}`)
    .join(' · ');

  const openFamily = (r) => navigate(`/discover/governance/${encodeURIComponent(r.family_name)}?from=apps`);

  return (
    <section className="mt-10" data-testid="rationalisation-progress-section">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <TrendingDown className="w-5 h-5 text-cyan-400" />
          Rationalisation Progress
          <span className="text-xs text-white/60 font-normal ml-2">
            Refreshed hourly and after each sync
          </span>
        </h2>
        <button
          type="button"
          onClick={() => navigate('/discover')}
          className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
          data-testid="rationalisation-progress-open-discover"
        >
          Open Discover <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* KPI strip */}
      <div className="alice-kpi-row grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="rationalisation-progress-kpis">
        <KPI
          label="In scope"
          value={fmt(scope.in_scope_families)}
          sub={hasBaseline
            ? <DeltaSince current={scope.in_scope_families} baselineValue={baseline.in_scope_families} baselineDate={baseline.date} />
            : 'app families being rationalised'}
          icon={Layers}
          accent={ACCENT.cyan}
          testid="kpi-rat-in-scope"
        />
        <KPI
          label="Excluded"
          value={fmt(scope.excluded_families)}
          sub={topReasons || 'not being rationalised'}
          icon={EyeOff}
          accent={ACCENT.slate}
          testid="kpi-rat-excluded"
        />
        <KPI
          label="Version debt"
          value={fmt(scope.version_debt)}
          sub={hasBaseline
            ? <DeltaSince current={scope.version_debt} baselineValue={baseline.version_debt} baselineDate={baseline.date} />
            : 'extra versions across in-scope families'}
          icon={GitBranch}
          accent={ACCENT.amber}
          testid="kpi-rat-version-debt"
        />
        <KPI
          label="Managed by ALICE"
          value={fmt(managed.families)}
          sub={`${fmt(managed.families_on_target)} fully on the released version`}
          icon={ShieldCheck}
          accent={ACCENT.emerald}
          testid="kpi-rat-managed"
        />
      </div>

      {/* Version control + trend */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className={CARD}>
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <Target className="w-4 h-4 text-emerald-400" />
              Version control
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <ControlFigure
              title={`Managed by ALICE · ${fmt(managed.families)} families`}
              pct={managed.on_target_pct}
              caption="of devices on the version ALICE released or newer. Devices in a rollout still in progress aren't counted against it."
              segments={[
                { label: 'On target',   value: managed.on_target,   color: ACCENT.emerald },
                { label: 'Rolling out', value: managed.rolling_out, color: ACCENT.blue },
                { label: 'Behind',      value: managed.behind,      color: ACCENT.rose },
              ]}
              measured={managed.measured}
              testid="rat-managed-control"
            />
            <div className="border-t border-[rgba(229,239,250,0.1)]" />
            <ControlFigure
              title={`Not managed · ${fmt(unmanaged.families)} in-scope families`}
              pct={unmanaged.on_latest_pct}
              caption="of devices on the newest version seen on the estate."
              segments={[
                { label: 'On newest', value: unmanaged.on_latest, color: ACCENT.cyan },
                { label: 'Behind',    value: unmanaged.behind,    color: ACCENT.amber },
              ]}
              measured={unmanaged.measured}
              testid="rat-unmanaged-control"
            />
          </CardContent>
        </Card>

        <Card className={`lg:col-span-2 ${CARD}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <TrendingDown className="w-4 h-4 text-cyan-400" />
              In-scope families and version debt
              {baseline && (
                <span className="text-xs text-white/60 font-normal ml-2">
                  Tracked since {fmtDate(baseline.date)}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {history.length >= 2 ? (
              <div className="h-64" data-testid="rat-trend-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={history} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={fmtShortDate}
                      tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      minTickGap={24}
                    />
                    <YAxis
                      tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                      width={48}
                    />
                    <Tooltip
                      contentStyle={TOOLTIP_STYLE.contentStyle}
                      itemStyle={TOOLTIP_STYLE.itemStyle}
                      labelStyle={TOOLTIP_STYLE.labelStyle}
                      labelFormatter={fmtDate}
                      formatter={(v) => fmt(v)}
                    />
                    <Legend wrapperStyle={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }} />
                    <Line type="monotone" dataKey="in_scope_families" name="In-scope families" stroke={ACCENT.cyan} strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="version_debt" name="Version debt" stroke={ACCENT.amber} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-white/60 text-sm py-6" data-testid="rat-trend-empty">
                The trend builds from {baseline ? fmtDate(baseline.date) : 'today'}, one point a day and after
                each sync. Both lines should fall as families are rationalised and versions consolidated.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Where to act */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className={CARD}>
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <AlertTriangle className="w-4 h-4 text-rose-400" />
              Managed apps off the released version
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <FamilyTable
              rows={data.lagging_managed || []}
              testid="rat-lagging-managed-table"
              onRow={openFamily}
              empty="Every device with an ALICE-managed app is on the released version, or in a rollout."
              columns={[
                { key: 'display_name', label: 'App', className: 'text-white' },
                { key: 'target_version', label: 'Released', render: (r) => `v${r.target_version}` },
                { key: 'behind', label: 'Behind', right: true, className: 'text-rose-400', render: (r) => fmt(r.behind) },
                { key: 'rolling_out', label: 'Rolling out', right: true, render: (r) => (r.rolling_out ? fmt(r.rolling_out) : '—') },
                {
                  key: 'on_target_pct', label: 'On target', right: true,
                  render: (r) => <span className="text-emerald-400 font-medium">{r.on_target_pct == null ? '—' : `${r.on_target_pct}%`}</span>,
                },
              ]}
            />
          </CardContent>
        </Card>

        <Card className={CARD}>
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <GitBranch className="w-4 h-4 text-amber-400" />
              Unmanaged apps with the most devices behind
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <FamilyTable
              rows={data.unmanaged_opportunities || []}
              testid="rat-unmanaged-opportunities-table"
              onRow={openFamily}
              empty="No unmanaged in-scope app has devices behind its newest version."
              columns={[
                { key: 'display_name', label: 'App', className: 'text-white' },
                { key: 'version_count', label: 'Versions', right: true },
                { key: 'latest_version', label: 'Newest', render: (r) => `v${r.latest_version}` },
                { key: 'behind', label: 'Behind', right: true, className: 'text-amber-400', render: (r) => fmt(r.behind) },
                { key: 'devices', label: 'Devices', right: true, render: (r) => fmt(r.devices) },
              ]}
            />
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default RationalisationProgress;
